import React from 'react';
import { Box, IconButton, MenuItem, TextField, Tooltip, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

const MESES = [
  'Janeiro', 'Fevereiro', 'Marco', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
];

interface CompetenciaSelectorProps {
  /** Competencia no formato 'AAAA-MM'. */
  competencia: string;
  onChange: (competencia: string) => void;
  /**
   * Ultima competencia com dados no banco ('AAAA-MM'). Quando informada, o seletor nao deixa
   * avancar alem dela — evita o usuario cair num mes vazio e achar que todas as equipes
   * zeraram, quando o que falta e o processamento da competencia.
   */
  maxCompetencia?: string | null;
  disabled?: boolean;
}

/** Ultima competencia FECHADA (mes anterior ao corrente), no formato 'AAAA-MM'. */
export function getCompetenciaFechada(): string {
  const hoje = new Date();
  const d = new Date(hoje.getFullYear(), hoje.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/** 'AAAA-MM' -> 'MM/AAAA'. */
export function formatCompetencia(competencia?: string | null): string {
  if (!competencia) return '-';
  const [ano, mes] = competencia.split('-');
  return `${mes}/${ano}`;
}

/** 'AAAA-MM' -> 'Agosto de 2025'. */
export function formatCompetenciaLonga(competencia?: string | null): string {
  if (!competencia) return '-';
  const [ano, mes] = competencia.split('-');
  const idx = Number(mes) - 1;
  return `${MESES[idx] || mes} de ${ano}`;
}

/** Desloca a competencia em N meses (N negativo volta no tempo). */
function deslocar(competencia: string, meses: number): string {
  const [ano, mes] = competencia.split('-').map(Number);
  const d = new Date(ano, mes - 1 + meses, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Persistencia durante a sessao, no mesmo espirito do QuadrimestreSelector: mantem a escolha
// do operador ao navegar; aba nova volta ao default (ultima competencia fechada).
const STORAGE_KEY = 'saude:competencia';

export function saveCompetencia(competencia: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, competencia);
  } catch { /* ignora quota/privacidade */ }
}

export function getCompetenciaInicial(): string {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw && /^\d{4}-(0[1-9]|1[0-2])$/.test(raw)) return raw;
  } catch { /* fallback abaixo */ }
  return getCompetenciaFechada();
}

/**
 * Seletor de competencia MM/AAAA. O modulo de saude so tinha o QuadrimestreSelector; a
 * verificacao de equipe zerada e apurada por mes fechado, nao por quadrimestre.
 */
const CompetenciaSelector: React.FC<CompetenciaSelectorProps> = ({
  competencia,
  onChange,
  maxCompetencia,
  disabled,
}) => {
  const [ano, mes] = competencia.split('-');
  const anoNum = Number(ano);

  const noLimite = !!maxCompetencia && competencia >= maxCompetencia;
  const avancar = (meses: number) => {
    const alvo = deslocar(competencia, meses);
    if (maxCompetencia && alvo > maxCompetencia) return;
    onChange(alvo);
  };

  const handleMes = (novoMes: string) => {
    const alvo = `${ano}-${novoMes}`;
    if (maxCompetencia && alvo > maxCompetencia) return;
    onChange(alvo);
  };

  const handleAno = (novoAno: number) => {
    const alvo = `${novoAno}-${mes}`;
    if (maxCompetencia && alvo > maxCompetencia) {
      onChange(maxCompetencia);
      return;
    }
    onChange(alvo);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Tooltip title="Competencia anterior">
        <IconButton size="small" onClick={() => avancar(-1)} disabled={disabled}>
          <ChevronLeft fontSize="small" />
        </IconButton>
      </Tooltip>

      <TextField
        select
        size="small"
        value={mes}
        onChange={(e) => handleMes(e.target.value)}
        disabled={disabled}
        sx={{ minWidth: 128, '& .MuiInputBase-input': { py: 0.75, fontSize: '0.85rem' } }}
      >
        {MESES.map((nome, i) => {
          const valor = String(i + 1).padStart(2, '0');
          const alvo = `${ano}-${valor}`;
          return (
            <MenuItem key={valor} value={valor} disabled={!!maxCompetencia && alvo > maxCompetencia}>
              {nome}
            </MenuItem>
          );
        })}
      </TextField>

      <Tooltip title="Ano anterior">
        <IconButton size="small" onClick={() => handleAno(anoNum - 1)} disabled={disabled}>
          <ChevronLeft fontSize="small" />
        </IconButton>
      </Tooltip>
      <Typography variant="body2" fontWeight={600} sx={{ minWidth: 38, textAlign: 'center' }}>
        {ano}
      </Typography>
      <Tooltip title="Proximo ano">
        <IconButton
          size="small"
          onClick={() => handleAno(anoNum + 1)}
          disabled={disabled || (!!maxCompetencia && `${anoNum + 1}-01` > maxCompetencia)}
        >
          <ChevronRight fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={noLimite ? 'Nao ha competencia posterior com dados' : 'Competencia seguinte'}>
        <IconButton size="small" onClick={() => avancar(1)} disabled={disabled || noLimite}>
          <ChevronRight fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default CompetenciaSelector;
